import React, { FC, useCallback, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

import {
	BUTTON_TYPES,
	EvButton,
	EvIssueDetails,
	EvIssueDetailsNew,
	IIssueElement,
	IIssueTag,
	IssueDetailsHelper,
	ScreenshotDetails
} from '@evinced-private/ui-common';

import { useGetScanIssue } from 'src/hooks/queries/useGetScanIssue';
import { IServerError } from 'src/interfaces/ServerError';
import { ScreenshotService } from 'src/services/ScreenshotService';

import { SITE_SCANNER_APP_ID } from '../../consts/dom-consts';
import { ClipboardHelper } from '../../helpers/ClipboardHelper';
import { RoutesHelper } from '../../helpers/RoutesHelper';
import { getDefaultSeverity } from '../../helpers/SeverityLabelHelper';
import { getBaseURL } from '../../helpers/UrlNormalizingHelper';
import { useConfiguration } from '../../providers/configurationProvider/ConfigurationProvider';
import { SiteScannerUiToggles } from '../../services/LocalTogglesService';
import { logger } from '../../services/Logger';
import { CopyToClipboard } from '../common/copy-to-clipboard/CopyToClipboard';
import { ITicketsSystemsHandle, TicketsSystems } from '../tickets-systems/TicketsSystems';

interface IIssueDetailsProps {
	issueId: string;
	scanId?: string;
	setError: (e: IServerError) => void;
	onClose?: () => void;
	setTitle?: (title: string) => void;
	isPage?: boolean;
}

export const IssueDetailsBlock: FC<IIssueDetailsProps> = ({
	issueId,
	scanId,
	setError,
	onClose,
	setTitle,
	isPage
}: IIssueDetailsProps) => {
	const { propertyId } = useParams<{ propertyId: string }>();
	const ticketsSystemsRef = useRef<ITicketsSystemsHandle>(null);

	const openIssueInNewTab = useCallback((): void => {
		window.open(RoutesHelper.getIssueDetailsPath(propertyId, scanId, issueId), '_blank');
	}, [propertyId, scanId, issueId]);

	const renderCopyToClipboardButton = useCallback(
		(textToCopy: string) => (
			<CopyToClipboard renderJustIcon textToCopy={textToCopy} iconTitle="Copy Selector" />
		),
		[]
	);

	const { data: issue, isLoading, error } = useGetScanIssue(scanId, issueId);

	const { getToggle, configuration } = useConfiguration();
	const shouldShowChatbotLink = getToggle(SiteScannerUiToggles.ENABLE_CHATBOT_LINK);
	const enableAzureIntegration = getToggle(SiteScannerUiToggles.ENABLE_AZURE_INTEGRATION);
	const showNewIssueDetailsModal = getToggle(SiteScannerUiToggles.SHOW_NEW_ISSUE_DETAILS_MODAL);
	const slackChatbotLinkConfig = configuration?.GLOBAL?.CONFIG?.SLACK_CHATBOT;
	const msTeamsChatbotLinkConfig = configuration?.GLOBAL?.CONFIG?.MS_TEAMS_CHATBOT;
	const chatbotLinkConfig = slackChatbotLinkConfig || msTeamsChatbotLinkConfig;

	useEffect(() => {
		if (issue && setTitle) {
			setTitle(issue?.type?.name);
		}
	}, [issue, setTitle]);

	useEffect(() => {
		if (error) {
			setError(error as IServerError);
			logger.warn('An error occurred while fetching single issue', error);
		}
	}, [error, setError]);

	const {
		description,
		type,
		severity,
		pageScreenshot,
		elements,
		summary,
		note,
		knowledgeBaseLink,
		customKnowledgeBaseLinks = [],
		tags
	} = issue || {};
	const { pageUrl } = elements?.[0] ?? {};
	const urlToCopy = `${getBaseURL()}${RoutesHelper.getIssueDetailsPath(
		propertyId,
		scanId,
		issueId
	)}`;

	const topRowActions = (
		<div className="action-buttons">
			<CopyToClipboard
				textToCopy={urlToCopy}
				buttonType={BUTTON_TYPES.GHOST}
				dataAutomationId="copy-issue-link-btn"
			/>
			{!isPage && (
				<EvButton type={BUTTON_TYPES.GHOST} onClick={openIssueInNewTab}>
					Open in a New Tab
				</EvButton>
			)}
			<TicketsSystems issue={issue} />
		</div>
	);

	const screenshotDetails: ScreenshotDetails = {
		pageScreenshotUrl: pageScreenshot,
		boundingBoxes: elements?.map((element) => element.boundingBox) || []
	};

	const issueDetails = {
		type: type?.name,
		severity: severity?.name,
		effect: IssueDetailsHelper.getEffectString(description || ''),
		cause: IssueDetailsHelper.getCauseString(description || ''),
		howToFix: IssueDetailsHelper.getHowToString(description || ''),
		summary,
		note,
		knowledgeBaseLink,
		pageUrl,
		screenshotDetails,
		level: tags?.[0]?.description,
		pageTitle: '',
		elements: elements as IIssueElement[],
		tags: tags as IIssueTag[],
		customKnowledgeBaseLinks
	};

	if (showNewIssueDetailsModal) {
		// `severity.name` holds the tenant's custom name when one is set (e.g. "High"). The badge colors
		// need the default severity, resolved from `newId`; the custom name is its label.
		const defaultSeverity = getDefaultSeverity(severity);

		return (
			<>
				{issue && <TicketsSystems ref={ticketsSystemsRef} issue={issue} hideAddToButtons />}
				<EvIssueDetailsNew
					issue={{
						...issueDetails,
						severity: defaultSeverity ?? severity?.name,
						// An empty label would blank the badge, so leave it unset and show the default name
						severityLabel: severity?.name || undefined
					}}
					isOpen
					onClose={isPage ? () => undefined : onClose}
					isFullPage={isPage}
					appElement={SITE_SCANNER_APP_ID}
					onOpenInNewTab={isPage ? undefined : openIssueInNewTab}
					onCopyLinkClick={() => ClipboardHelper.copyToClipboard(urlToCopy)}
					showAddToJiraButton={!isPage && !enableAzureIntegration}
					onAddToJiraClick={() => ticketsSystemsRef.current?.openJira()}
					showAddToAzureButton={!isPage && enableAzureIntegration}
					onAddToAzureClick={() => ticketsSystemsRef.current?.openAzure()}
					isLoading={isLoading}
					screenshotService={ScreenshotService}
					logger={logger}
					copyToClipboardButton={renderCopyToClipboardButton}
				/>
			</>
		);
	}

	return (
		<EvIssueDetails
			issue={issueDetails}
			isLoading={isLoading}
			isFullPageMode={isPage}
			topRowActions={topRowActions}
			screenshotService={ScreenshotService}
			logger={logger}
			copyToClipboardButton={renderCopyToClipboardButton}
			showChatbotLink={shouldShowChatbotLink}
			chatbotLinkConfig={chatbotLinkConfig}
		/>
	);
};
